// JobMetricsChart: live CPU + memory usage for a single running job.
// Server pushes one `job_metrics:sample` event every ~100ms while the
// job is in `BuildSlotRunning` on the runner. The hook keeps a rolling
// window of the last MAX_SAMPLES samples and renders them as two
// stacked LINE plots: CPU on top, MEM on the bottom, separated by a
// thin dashed divider. Both series grow UP from their own 0% baseline
// at the bottom of their region toward a dynamic axis max at the top.
//
// CPU is cumulative cgroup `cpu.stat usage_usec`; we difference
// successive samples for a per-core utilisation %. Memory is
// point-in-time `memory.current` rendered as a fraction of the
// runner's total RAM.
//
// Color encodes value: each sample's point is colored on a piecewise
// interpolation from the series' base color (info for CPU, warning for
// MEM) through warning to danger at 100%. Realised in SVG via a
// `<linearGradient>` with one stop per sample at that sample's x —
// SVG does the spatial interpolation natively, so a spike turns red
// only at the spike's x position rather than flooding the whole line.
//
// First-paint of the line fades in via a CSS `@keyframes` on the
// `.metrics-line` class so the line doesn't slam into existence.

import { type Selection } from "d3-selection";
import { scaleLinear, type ScaleLinear } from "d3-scale";
import { max as d3max } from "d3-array";
import type { LiveViewHook, CiPushEventWire } from "../types";
import type { JobSample } from "../../../../.dox/ci";
import { cssVar } from "./theme";
import { buildGradientStops } from "./metrics-axes";
import { redrawMetrics } from "./metrics-render";
import {
  DataAttr,
  readPositiveNumberData,
  requireHookCanvas,
} from "./hook-dom-contract";

// Re-export the paradox-generated record so the rest of the chart code
// (and tests) consumes the dox source of truth. A future rename of any
// field in `ci-runner.dox`'s `JobSample` becomes a tsc error here rather
// than a silent runtime hole where the chart paints zeros.
export type { JobSample };

// Canonical CPU/MEM/TS triple used by the pure compute helpers below.
// Structurally a subset of `JobSample` (drops the `jobId` discriminator
// the math doesn't care about); the runner-host chart (`HostSample`-fed)
// projects its own wire payload into this shape at its handler boundary
// so both chart families share one math pipeline. Branded BigNatural /
// RamBytes from dox widen to `number` here without a runtime cast.
export interface MetricsSample {
  readonly cpuUsageUs: number;
  readonly memoryBytes: number;
  readonly tsMs: number;
}

// Discriminated union — replaces the `number | null` shape so every
// consumer is forced to acknowledge the "no delta yet" state explicitly
// instead of doing `cpuPct ?? 0` and silently rendering zeros for
// samples that don't have a predecessor.
export type CpuPct =
  | { readonly kind: "valid"; readonly v: number }
  | { readonly kind: "pending" };

export const cpuPending: CpuPct = { kind: "pending" };
export const cpuValid = (v: number): CpuPct => ({ kind: "valid", v });

export interface ChartPoint {
  cpuPct: CpuPct;
  memPct: number;
}

// CPU-only render point with its index into the parent `chart` array.
// The index is what the gradient-stop math needs to anchor the stop at
// the same x as the line vertex — without it, a future change to the
// indexedCpu projection could silently drift line and gradient apart.
export interface IndexedCpuPoint {
  i: number;
  cpuPct: number;
  memPct: number;
}

export const indexedCpu = (
  chart: ReadonlyArray<ChartPoint>,
): IndexedCpuPoint[] =>
  chart.flatMap((p, i) =>
    p.cpuPct.kind === "valid"
      ? [{ i, cpuPct: p.cpuPct.v, memPct: p.memPct }]
      : [],
  );

interface JobMetricsChartHook extends LiveViewHook<CiPushEventWire> {
  _samples: MetricsSample[];
  _chart: ChartPoint[];
  _lastChart?: ChartPoint[] | undefined;
  _lastCpuMax?: number | undefined;
  _lastMemMax?: number | undefined;
  _cores: number;
  _ramBytes: number;
  _ramps?: { cpu: Rgb[]; mem: Rgb[] } | undefined;
  _canvas?: HTMLCanvasElement | undefined;
  _ctx?: CanvasRenderingContext2D | undefined;
  _w: number;
  _h: number;
  _rafId?: number | undefined;
  _ro?: ResizeObserver | undefined;
  _lastSampleTsMs?: number | undefined;
  _scrollStartedAtMs?: number | undefined;
  _scrollDurationMs?: number | undefined;
  _scrollStartOffsetSlots?: number | undefined;
  _scrollOffsetSlots?: number | undefined;
  redraw(this: JobMetricsChartHook): void;
  scheduleRedraw(this: JobMetricsChartHook): void;
}

// Display window — 60 s @ 10 Hz.
export const MAX_SAMPLES = 600;
// Ring keeps two extra samples on the left: one CPU delta predecessor and one
// render-only edge anchor so the scrolling line exits behind the Y axis.
export const RING_CAP = MAX_SAMPLES + 2;
const MARGIN = { top: 4, right: 4, bottom: 4, left: 56 };
const CENTRELINE_GAP = 6;

// Pure CPU-pct from one sample + the nearest usable predecessor. Returns
// `pending` until we have a viable delta — same semantics as the old
// `number | null` return, just typed so callers can't forget the gap.
const cpuPctFromSamples = (
  samples: ReadonlyArray<MetricsSample>,
  i: number,
  cores: number,
): CpuPct => {
  if (cores <= 0) return cpuPending;
  const s = samples[i]!;
  // Walk back to the nearest prior sample that produces a usable delta.
  // Any clock-granularity hiccup or out-of-order PubSub delivery used to
  // drop a hole into the middle of the line — walk back further to
  // recover, common case is one step.
  for (let j = i - 1; j >= 0; j--) {
    const prev = samples[j]!;
    const dT = s.tsMs - prev.tsMs;
    const dCpu = s.cpuUsageUs - prev.cpuUsageUs;
    if (dT > 0 && dCpu >= 0) {
      const pct = (dCpu / 1000 / dT / cores) * 100;
      return cpuValid(Math.max(0, Math.min(100, pct)));
    }
  }
  return cpuPending;
};

export const computeChartWindow = (
  samples: ReadonlyArray<MetricsSample>,
  cores: number,
  ramBytes: number,
  maxSamples: number,
): ChartPoint[] => {
  if (samples.length === 0) return [];
  const start = Math.max(0, samples.length - maxSamples);
  return samples.slice(start).map((s, k) => {
    const i = start + k;
    const memPct =
      ramBytes > 0
        ? Math.max(0, Math.min(100, (s.memoryBytes / ramBytes) * 100))
        : 0;
    return { cpuPct: cpuPctFromSamples(samples, i, cores), memPct };
  });
};

export const computeChart = (
  samples: ReadonlyArray<MetricsSample>,
  cores: number,
  ramBytes: number,
): ChartPoint[] => computeChartWindow(samples, cores, ramBytes, MAX_SAMPLES);

export type AxisScale = ScaleLinear<number, number>;

export const metricsNearestIndex = (
  n: number,
  mx: number,
  w: number,
): number => {
  if (n <= 1) return 0;
  const innerW = w - MARGIN.left - MARGIN.right;
  const stepW = innerW / MAX_SAMPLES;
  const offset = MAX_SAMPLES - n;
  const raw = (mx - MARGIN.left) / stepW - offset - 0.5;
  return Math.max(0, Math.min(n - 1, Math.round(raw)));
};

export const formatMetricsHoverLabel = (p: ChartPoint): string => {
  const cpu = p.cpuPct.kind === "valid" ? `${Math.round(p.cpuPct.v)}%` : "--";
  const mem = `${Math.round(p.memPct)}%`;
  return `CPU ${cpu} · MEM ${mem}`;
};

export const seriesMax = (
  chart: ReadonlyArray<ChartPoint>,
  field: "cpuPct" | "memPct",
): number =>
  d3max(chart, (p) =>
    field === "cpuPct"
      ? p.cpuPct.kind === "valid"
        ? p.cpuPct.v
        : undefined
      : p.memPct,
  ) ?? 0;

export const formatMetricsAxisPct = (v: number): string => {
  if (!Number.isFinite(v) || v <= 0) return "0%";
  if (v < 1) return "<1%";
  return `${Math.round(v)}%`;
};

// Absolute-unit formatters for the *per-job* chart. The job runs on one
// (possibly large) shared runner, so a "% of runner total" axis collapses
// every job to <1% — useless for spotting a job pegging a single core or
// using a few GiB. Convert the (% of runner) max back to an absolute via
// the runner denominators and format directly.
const formatBytes = (b: number): string => {
  if (!Number.isFinite(b) || b <= 0) return "0 B";
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GiB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MiB`;
  if (b >= 1024) return `${(b / 1024).toFixed(1)} KiB`;
  return `${Math.round(b)} B`;
};

export const formatJobCpuAxis = (cpuMaxPct: number, cores: number): string => {
  if (!Number.isFinite(cpuMaxPct) || cpuMaxPct <= 0 || cores <= 0) {
    return "0 cores";
  }
  const cpuCores = (cpuMaxPct / 100) * cores;
  if (cpuCores < 0.01) return "0 cores";
  if (cpuCores < 1) return `${cpuCores.toFixed(2)} cores`;
  if (cpuCores < 10) return `${cpuCores.toFixed(1)} cores`;
  return `${Math.round(cpuCores)} cores`;
};

export const formatJobMemAxis = (
  memMaxPct: number,
  ramBytes: number,
): string => {
  if (!Number.isFinite(memMaxPct) || memMaxPct <= 0 || ramBytes <= 0) {
    return "0 B";
  }
  return formatBytes((memMaxPct / 100) * ramBytes);
};

const buildRegionScale = (
  domainMax: number,
  baseY: number,
  topY: number,
): AxisScale =>
  scaleLinear()
    .domain([0, domainMax > 0 ? domainMax : 1])
    .range([baseY, topY])
    .clamp(true);

export const cpuRegionScale = (max: number, h: number): AxisScale => {
  const innerH = h - MARGIN.top - MARGIN.bottom;
  const regionH = Math.max(0, (innerH - CENTRELINE_GAP) / 2);
  return buildRegionScale(max, MARGIN.top + regionH, MARGIN.top);
};

export const memRegionScale = (max: number, h: number): AxisScale => {
  const innerH = h - MARGIN.top - MARGIN.bottom;
  const regionH = Math.max(0, (innerH - CENTRELINE_GAP) / 2);
  return buildRegionScale(
    max,
    MARGIN.top + innerH,
    MARGIN.top + regionH + CENTRELINE_GAP,
  );
};

// x coordinate (centre) for the sample at index `i` of a right-anchored
// window of length `n` within an SVG of width `w`. Centre-of-slot so the
// rightmost sample lands a half-step from the right edge (mirrors the
// prior bar-renderer's right-anchored layout — a half-empty window
// fills from the right instead of squashing to the left).
export const pointX = (i: number, n: number, w: number): number => {
  const innerW = w - MARGIN.left - MARGIN.right;
  const stepW = innerW / MAX_SAMPLES;
  const offset = MAX_SAMPLES - n;
  return MARGIN.left + (i + offset + 0.5) * stepW;
};

export type Rgb = readonly [number, number, number];

// Parse the `cssVar` output ("0 191 255") into an RGB triple.
// Returns `[0, 0, 0]` on any malformed input — the theme variables are
// our own and the only realistic failure mode is "theme stylesheet
// hasn't loaded yet", which paints black; a future paint will swap in
// the real value once redraw runs.
export const parseRgbTriple = (v: string): Rgb => {
  const parts = v.trim().split(/\s+/);
  const r = Number(parts[0]);
  const g = Number(parts[1]);
  const b = Number(parts[2]);
  return [
    Number.isFinite(r) ? r : 0,
    Number.isFinite(g) ? g : 0,
    Number.isFinite(b) ? b : 0,
  ] as const;
};

export const lerpRgb = (from: Rgb, to: Rgb, t: number): Rgb => {
  const u = Math.max(0, Math.min(1, t));
  return [
    Math.round(from[0] + (to[0] - from[0]) * u),
    Math.round(from[1] + (to[1] - from[1]) * u),
    Math.round(from[2] + (to[2] - from[2]) * u),
  ] as const;
};

// Piecewise interpolate `pct` (0–100) across `stops` (≥ 1) evenly
// spaced from low to high. Returns an `rgb(r g b)` CSS string ready
// for an SVG `stop-color`. Two stops give a straight base→danger ramp;
// three stops (base → warm midpoint → danger) avoid the muddy purple
// midpoint when the base is blue. Same shape for CPU and MEM keeps
// the colour treatment consistent across the two regions.
export const valueColor = (pct: number, stops: ReadonlyArray<Rgb>): string => {
  if (stops.length === 0) return "rgb(0 0 0)";
  if (stops.length === 1) {
    const [r, g, b] = stops[0]!;
    return `rgb(${r} ${g} ${b})`;
  }
  const u = Math.max(0, Math.min(100, pct)) / 100;
  const seg = 1 / (stops.length - 1);
  const idx = Math.min(stops.length - 2, Math.floor(u / seg));
  const t = (u - idx * seg) / seg;
  const [r, g, b] = lerpRgb(stops[idx]!, stops[idx + 1]!, t);
  return `rgb(${r} ${g} ${b})`;
};

export interface GradientStop {
  offset: number; // 0..1 along the gradient axis
  color: string; // "rgb(r g b)"
}

// Build one stop per sample at that sample's x position along the
// gradient. The gradient axis is `[MARGIN.left, w - MARGIN.right]` so
// offset = (pointX(i, n, w) - MARGIN.left) / innerW. Returning [] is
// fine — the path renders with the path's `stroke="none"` fallback
// fill, which is invisible (intentional: idle window = no line).
export const gradientStops = (
  points: ReadonlyArray<{ i: number; pct: number }>,
  n: number,
  w: number,
  stops: ReadonlyArray<Rgb>,
): GradientStop[] => {
  if (points.length === 0) return [];
  const innerW = w - MARGIN.left - MARGIN.right;
  if (innerW <= 0) return [];
  return points.map((p) => ({
    offset: (pointX(p.i, n, w) - MARGIN.left) / innerW,
    color: valueColor(p.pct, stops),
  }));
};

export const ensureAxis = (
  svg: Selection<SVGSVGElement, unknown, null, undefined>,
): {
  axis: Selection<SVGGElement, unknown, null, undefined>;
  centreline: Selection<SVGLineElement, unknown, null, undefined>;
  baseline: Selection<SVGLineElement, unknown, null, undefined>;
  cpuLabel: Selection<SVGTextElement, unknown, null, undefined>;
  memLabel: Selection<SVGTextElement, unknown, null, undefined>;
} => {
  let axis = svg.select<SVGGElement>("g.axis");
  if (axis.empty()) {
    axis = svg.append("g").attr("class", "axis");
  }

  let centreline = axis.select<SVGLineElement>("line.axis-centreline");
  if (centreline.empty()) {
    centreline = axis.append("line").attr("class", "axis-centreline");
  }

  let baseline = axis.select<SVGLineElement>("line.axis-baseline");
  if (baseline.empty()) {
    baseline = axis.append("line").attr("class", "axis-baseline");
  }

  let cpuLabel = axis.select<SVGTextElement>("text.axis-cpu-label");
  if (cpuLabel.empty()) {
    cpuLabel = axis.append("text").attr("class", "axis-cpu-label");
  }

  let memLabel = axis.select<SVGTextElement>("text.axis-mem-label");
  if (memLabel.empty()) {
    memLabel = axis.append("text").attr("class", "axis-mem-label");
  }

  return { axis, centreline, baseline, cpuLabel, memLabel };
};

export const drawAxis = (
  axis: {
    centreline: Selection<SVGLineElement, unknown, null, undefined>;
    baseline: Selection<SVGLineElement, unknown, null, undefined>;
    cpuLabel: Selection<SVGTextElement, unknown, null, undefined>;
    memLabel: Selection<SVGTextElement, unknown, null, undefined>;
  },
  w: number,
  h: number,
  cpuMax: number,
  memMax: number,
): void => {
  const innerH = h - MARGIN.top - MARGIN.bottom;
  const regionH = Math.max(0, (innerH - CENTRELINE_GAP) / 2);
  const cpuTopY = MARGIN.top;
  const cpuBaseY = MARGIN.top + regionH;
  const memTopY = cpuBaseY + CENTRELINE_GAP;

  const tickColor = "rgba(255,255,255,1)";
  const lineColor = `rgb(${cssVar("--c-border")} / 0.4)`;

  // Centreline gap between the CPU region (above) and MEM region (below).
  axis.centreline
    .attr("x1", MARGIN.left)
    .attr("x2", w - MARGIN.right)
    .attr("y1", cpuBaseY + CENTRELINE_GAP / 2)
    .attr("y2", cpuBaseY + CENTRELINE_GAP / 2)
    .attr("stroke", lineColor)
    .attr("stroke-dasharray", "2,2");

  // Baseline along the bottom of the chart drawing area — aligned with
  // the bottom of the Y axis (= MEM region's 0%). Same subtle dotted
  // style as the centreline so the two read as paired baselines for
  // the two stacked regions.
  const bottomY = h - MARGIN.bottom;
  axis.baseline
    .attr("x1", MARGIN.left)
    .attr("x2", w - MARGIN.right)
    .attr("y1", bottomY)
    .attr("y2", bottomY)
    .attr("stroke", lineColor)
    .attr("stroke-dasharray", "2,2");

  for (const [label, y, text] of [
    [axis.cpuLabel, cpuTopY, `CPU ${formatMetricsAxisPct(cpuMax)}`] as const,
    [axis.memLabel, memTopY, `MEM ${formatMetricsAxisPct(memMax)}`] as const,
  ]) {
    label
      .attr("x", MARGIN.left - 4)
      .attr("y", y + 3)
      .attr("text-anchor", "end")
      .attr("font-size", "9")
      .attr("fill", tickColor)
      .text(text);
  }
};

const parseInitialSamples = (el: HTMLElement): MetricsSample[] => {
  const raw = el.dataset["initialSamples"];
  if (!raw || raw === "[]") return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MetricsSample[]) : [];
  } catch {
    return [];
  }
};

// Series color ramps. Pure shape construction lives in
// metrics-axes.buildGradientStops — we just resolve the theme triple
// here and hand it off.
const buildRamps = (): { cpu: Rgb[]; mem: Rgb[] } => {
  const stops = buildGradientStops({
    info: parseRgbTriple(cssVar("--c-info")),
    warning: parseRgbTriple(cssVar("--c-warning")),
    danger: parseRgbTriple(cssVar("--c-danger")),
  });
  return { cpu: [...stops.cpu], mem: [...stops.mem] };
};

interface MetricsAnimationHook {
  _lastSampleTsMs?: number | undefined;
  _scrollStartedAtMs?: number | undefined;
  _scrollDurationMs?: number | undefined;
  _scrollStartOffsetSlots?: number | undefined;
  _scrollOffsetSlots?: number | undefined;
}

const DEFAULT_SCROLL_MS = 120;
const MIN_SCROLL_MS = 80;
const MAX_SCROLL_MS = 220;

const clampScrollMs = (ms: number): number =>
  Math.max(MIN_SCROLL_MS, Math.min(MAX_SCROLL_MS, ms));

const currentMetricsScrollOffsetSlots = (
  hook: MetricsAnimationHook,
  nowMs: number,
): number => {
  if (
    hook._scrollStartedAtMs == null ||
    hook._scrollDurationMs == null ||
    hook._scrollStartOffsetSlots == null
  ) {
    return hook._scrollOffsetSlots ?? 0;
  }

  const progress = Math.max(
    0,
    Math.min(1, (nowMs - hook._scrollStartedAtMs) / hook._scrollDurationMs),
  );
  return hook._scrollStartOffsetSlots * (1 - progress);
};

export const noteMetricsSampleForScroll = (
  hook: MetricsAnimationHook,
  tsMs: number,
): void => {
  const now = performance.now();
  const currentOffsetSlots = currentMetricsScrollOffsetSlots(hook, now);
  const nextOffsetSlots = 1 + currentOffsetSlots;
  const interval =
    hook._lastSampleTsMs != null ? tsMs - hook._lastSampleTsMs : 0;
  hook._lastSampleTsMs = tsMs;
  hook._scrollDurationMs =
    clampScrollMs(interval > 0 ? interval : DEFAULT_SCROLL_MS) *
    nextOffsetSlots;
  hook._scrollStartedAtMs = now;
  hook._scrollStartOffsetSlots = nextOffsetSlots;
  hook._scrollOffsetSlots = nextOffsetSlots;
};

export const resetMetricsScroll = (hook: MetricsAnimationHook): void => {
  hook._lastSampleTsMs = undefined;
  hook._scrollStartedAtMs = undefined;
  hook._scrollDurationMs = undefined;
  hook._scrollStartOffsetSlots = undefined;
  hook._scrollOffsetSlots = 0;
};

export const updateMetricsScrollProgress = (
  hook: MetricsAnimationHook,
  nowMs: number,
): boolean => {
  if (hook._scrollStartedAtMs == null || hook._scrollDurationMs == null) {
    hook._scrollOffsetSlots = 0;
    return false;
  }

  const offsetSlots = currentMetricsScrollOffsetSlots(hook, nowMs);
  hook._scrollOffsetSlots = offsetSlots;
  if (offsetSlots > 0) return true;

  hook._scrollStartedAtMs = undefined;
  hook._scrollDurationMs = undefined;
  hook._scrollStartOffsetSlots = undefined;
  return false;
};

export const JobMetricsChart = {
  mounted(this: JobMetricsChartHook) {
    this._samples = parseInitialSamples(this.el);
    this._chart = [];
    this._cores = readPositiveNumberData(
      this.el,
      "jobMetricsChart",
      DataAttr.cores,
    );
    this._ramBytes = readPositiveNumberData(
      this.el,
      "jobMetricsChart",
      DataAttr.ram_bytes,
    );
    this._ramps = buildRamps();
    this._rafId = undefined;
    resetMetricsScroll(this);

    if (this._cores === 0 || this._ramBytes === 0) {
      console.warn("JobMetricsChart: missing or invalid runner denominators", {
        cores: this.el.dataset["cores"],
        ramBytes: this.el.dataset["ramBytes"],
      });
    }

    const rect = this.el.getBoundingClientRect();
    this._w = rect.width || 320;
    this._h = rect.height || 80;

    const canvas = requireHookCanvas(this.el, "jobMetricsChart");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    this._canvas = canvas;
    this._ctx = ctx;

    this._ro = new ResizeObserver(() => {
      const r = this.el.getBoundingClientRect();
      this._w = r.width || this._w;
      this._h = r.height || this._h;
      this._lastChart = undefined;
      this.scheduleRedraw();
    });
    this._ro.observe(this.el);

    this.scheduleRedraw();

    this.handleEvent("jobMetricsSample", (raw: unknown) => {
      // Wire shape matches `Dox.JobSample` minus the `jobId`
      // discriminator (subscription topic already implies the job).
      const s = raw as MetricsSample;
      this._samples = ringPush(this._samples, s, RING_CAP);
      noteMetricsSampleForScroll(this, s.tsMs);
      this.scheduleRedraw();
    });

    this.handleEvent("jobMetricsLoad", (raw: unknown) => {
      const payload = raw as { samples?: MetricsSample[] };
      const samples = Array.isArray(payload.samples) ? payload.samples : [];
      this._samples =
        samples.length > RING_CAP
          ? samples.slice(samples.length - RING_CAP)
          : [...samples];
      this._lastChart = undefined;
      resetMetricsScroll(this);
      this.scheduleRedraw();
    });
  },

  scheduleRedraw(this: JobMetricsChartHook) {
    if (this._rafId != null) return;
    this._rafId = requestAnimationFrame((nowMs) => {
      this._rafId = undefined;
      const keepAnimating = updateMetricsScrollProgress(this, nowMs);
      this.redraw();
      if (keepAnimating) this.scheduleRedraw();
    });
  },

  redraw(this: JobMetricsChartHook) {
    redrawMetrics(this, {
      idBase: this.el.id || "job-metrics",
      rampsFallback: buildRamps,
      formatCpuAxis: formatJobCpuAxis,
      formatMemAxis: formatJobMemAxis,
    });
  },

  destroyed(this: JobMetricsChartHook) {
    if (this._rafId != null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = undefined;
    }
    this._ro?.disconnect();
    this._ro = undefined;
    this._canvas = undefined;
    this._ctx = undefined;
    this._ramps = undefined;
    this._samples = [];
    this._chart = [];
    this._lastChart = undefined;
    this._lastCpuMax = undefined;
    this._lastMemMax = undefined;
    resetMetricsScroll(this);
  },
};

export const ringPush = <T>(
  buf: ReadonlyArray<T>,
  item: T,
  cap: number,
): T[] => (buf.length < cap ? [...buf, item] : [...buf.slice(1), item]);
